/**
 * li-selling-plan.js
 * Custom element <li-selling-plan-widget> for Johnny Slicks subscription widget.
 *
 * Features:
 * - Form injection: finds product-form-component form and injects hidden selling_plan input
 * - Frequency selection: updates hidden input with selected plan.id
 * - Purchase type toggle: one-time (empty) vs subscribe (plan ID)
 * - Variant sync: listens for variant:update to recalculate prices & guard the input
 * - Main price sync: updates the <product-price> block when toggling purchase type or variant
 * - MutationObserver guards: prevents Horizon DOM morphing from clearing selling_plan
 * - URL sync: reads/writes ?selling_plan= for persistence across refreshes
 * - Defaults to subscription with first selling plan
 */

if (!customElements.get('li-selling-plan-widget')) {
  class LiSellingPlanWidget extends HTMLElement {
    constructor() {
      super();

      // State
      this.selectedPlan = '';
      this.allowClear = false;
      this.currentVariant = '';
      this._lastVariantPrice = 0;
      this._lastVariantCompareAt = 0;

      // Bound methods
      this._onOneTimeChange = this._onOneTimeChange.bind(this);
      this._onSubscribeChange = this._onSubscribeChange.bind(this);
      this._onFreqClick = this._onFreqClick.bind(this);
      this._onVariantUpdate = this._onVariantUpdate.bind(this);
    }

    connectedCallback() {
      // Read currency config from data attributes
      this._moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this._currency = this.dataset.currency || 'USD';

      // Cache DOM references
      this._cacheDOMRefs();

      // Load selling plan data from JSON island
      this._loadPlanData();

      // Read URL params
      const urlParams = new URLSearchParams(window.location.search);
      const urlSellingPlan = urlParams.get('selling_plan');
      const urlVariant = urlParams.get('variant');

      this.currentVariant = urlVariant || this._defaultVariantId || '';

      // Store initial variant price from plan data
      this._storeVariantPrice();

      // Ensure hidden input exists in the product form
      this._ensureHiddenInput();

      // Wire events
      this.oneTimeRadio?.addEventListener('change', this._onOneTimeChange);
      this.subscribeRadio?.addEventListener('change', this._onSubscribeChange);
      this.freqButtons.forEach(btn => {
        btn.addEventListener('click', this._onFreqClick);
      });

      // Listen for variant:update from Horizon's events.js
      document.addEventListener('variant:update', this._onVariantUpdate);

 // Set initial state
      if (urlSellingPlan) {
        this.selectedPlan = urlSellingPlan;
        this.subscribeRadio.checked = true;
        this._initSubscriptionUI(urlSellingPlan);
      } else {
        // No selling_plan in URL (with or without variant) → default to subscribe with first plan
        this.subscribeRadio.checked = true;
        this._initSubscriptionUI();
      }

      this._updateOptionStyles();

      // Style listener for radio changes
      this.querySelectorAll('input[name="li_purchase_type"]').forEach(r => {
        r.addEventListener('change', () => this._updateOptionStyles());
      });

      // Install guards against unwanted clearing
      this._installGuards();

      // Final enforcement after other theme scripts run
      requestAnimationFrame(() => {
        const input = this._getSellingInput();
        if (this.subscribeRadio?.checked) {
          if (urlSellingPlan) {
            this._updateSellingPlan(urlSellingPlan);
            const btn = this.freqButtons.find(b => b.dataset.planId === urlSellingPlan);
            if (btn) {
              this.freqButtons.forEach(b => b.classList.remove('is-active'));
              btn.classList.add('is-active');
            }
          } else if (input && !input.value && this.freqButtons.length > 0) {
            this.freqButtons[0].click();
          }
        }

        // Set initial main price based on purchase type
        this._updateMainPrice();
      });
    }

    disconnectedCallback() {
      this.oneTimeRadio?.removeEventListener('change', this._onOneTimeChange);
      this.subscribeRadio?.removeEventListener('change', this._onSubscribeChange);
      this.freqButtons.forEach(btn => {
        btn.removeEventListener('click', this._onFreqClick);
      });
      document.removeEventListener('variant:update', this._onVariantUpdate);
      if (this._domObserver) {
        this._domObserver.disconnect();
      }
    }

    /* ------------------------------------------------------------------
       DOM Reference Management
       ------------------------------------------------------------------ */

    _cacheDOMRefs() {
      this.oneTimeRadio = this.querySelector('[data-purchase-type="onetime"]');
      this.subscribeRadio = this.querySelector('[data-purchase-type="subscribe"]');
      this.freqSection = this.querySelector('[data-selling-plan-frequencies]');
      this.freqButtons = Array.from(this.querySelectorAll('[data-plan-id]'));
      this.priceCompare = this.querySelector('[data-onetime-price-compare]');
      this.priceCurrent = this.querySelector('[data-onetime-price]');
      this.subPriceCompare = this.querySelector('[data-sub-price-compare]');
      this.subPriceCurrent = this.querySelector('[data-sub-price]');
    }

    /* ------------------------------------------------------------------
       Data Loading
       ------------------------------------------------------------------ */

    _loadPlanData() {
      const script = this.querySelector('script[type="application/json"][data-selling-plans]');
      if (!script) return;
      try {
        this._planData = JSON.parse(script.textContent);
        this._defaultVariantId = this._planData.defaultVariantId;
      } catch (e) {
        console.error('[li-selling-plan] Failed to parse plan data', e);
      }
    }

    _storeVariantPrice() {
      if (!this._planData?.allocations || !this.currentVariant) return;
      const alloc = this._planData.allocations[String(this.currentVariant)];
      if (alloc && alloc.length > 0) {
        // compareAtPrice in allocation data = the variant's base price (before subscription discount)
        this._lastVariantPrice = alloc[0].compareAtPrice || 0;
        // Get the actual Shopify compare-at price from the DOM data attribute
        const liBlock = document.querySelector('.li-price-block');
        if (liBlock && liBlock.dataset.liCmp) {
          this._lastVariantCompareAt = parseInt(liBlock.dataset.liCmp, 10) || 0;
        } else {
          this._lastVariantCompareAt = 0;
        }
      }
    }

    /* ------------------------------------------------------------------
       Hidden Input Management
       ------------------------------------------------------------------ */

    _getSellingInput() {
      // Look for the hidden input inside the product form
      const form = document.querySelector('product-form-component form');
      if (!form) return null;
      return form.querySelector('input[name="selling_plan"]');
    }

    _ensureHiddenInput() {
      const form = document.querySelector('product-form-component form');
      if (!form) return;

      let input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.value = '';
        form.appendChild(input);
      }
    }

    /* ------------------------------------------------------------------
       Selling Plan Value Management
       ------------------------------------------------------------------ */

    _updateSellingPlan(planId) {
      const input = this._getSellingInput();
      if (!input) {
        // Input may have been removed by morph — re-inject and try again
        this._ensureHiddenInput();
        const retryInput = this._getSellingInput();
        if (!retryInput) return;
        this.selectedPlan = planId;
        retryInput.value = planId;
        retryInput.setAttribute('value', planId);
        this._syncURL();
        this._installGuards();
        return;
      }
      this.selectedPlan = planId;
      input.value = planId;
      input.setAttribute('value', planId);
      this._syncURL();
    }

    _syncURL() {
      const url = new URL(window.location.href);
      const input = this._getSellingInput();
      if (input?.value) {
        url.searchParams.set('selling_plan', input.value);
      } else {
        url.searchParams.delete('selling_plan');
      }
      if (this.currentVariant) {
        url.searchParams.set('variant', this.currentVariant);
      }
      history.replaceState(null, '', url);
    }

    /* ------------------------------------------------------------------
       UI State
       ------------------------------------------------------------------ */

    _initSubscriptionUI(planToUse) {
      const input = this._getSellingInput();
      if (!input) this._ensureHiddenInput();

      if (this.freqSection) this.freqSection.classList.add('is-visible');
      this.freqButtons.forEach(b => b.classList.remove('is-active'));

      const plan = planToUse || this.selectedPlan || this.freqButtons[0]?.dataset.planId;
      const btn = this.freqButtons.find(b => b.dataset.planId === plan) || this.freqButtons[0];
      if (!btn) return;

      btn.classList.add('is-active');
      this._updateSellingPlan(btn.dataset.planId);
    }

    _handleOneTime(isTrusted) {
      this.freqButtons.forEach(b => b.classList.remove('is-active'));
      if (this.freqSection) this.freqSection.classList.remove('is-visible');

      this.allowClear = isTrusted;
      if (this.allowClear) {
        const input = this._getSellingInput();
        if (input) {
          input.value = '';
          input.setAttribute('value', '');
        }
        this._syncURL();
      }
      setTimeout(() => { this.allowClear = false; }, 0);
    }

    _updateOptionStyles() {
      this.querySelectorAll('.li-selling-plan__option').forEach(o => {
        o.classList.remove('is-active');
      });
      const checked = this.querySelector('input[name="li_purchase_type"]:checked');
      checked?.closest('.li-selling-plan__option')?.classList.add('is-active');
    }

    /* ------------------------------------------------------------------
       Main Price Sync — updates <product-price> block under title
       ------------------------------------------------------------------ */

    _updateMainPrice() {
      // Try LI Price block first, fall back to original product-price
      let container = document.querySelector('.li-price-block');
      let priceEl = container?.querySelector('.li-price__amount');
      let compareEl = container?.querySelector('.li-price__compare');
      let badgeEl = container?.querySelector('.li-price__badge');
      let isLiPrice = true;

      if (!priceEl) {
        // Fallback to original product-price element
        container = document.querySelector('product-price');
        priceEl = container?.querySelector('.price');
        compareEl = container?.querySelector('.compare-at-price');
        isLiPrice = false;
      }

      if (!priceEl) return;

      const compareClass = isLiPrice ? 'li-price__compare' : 'compare-at-price';
      const priceContainer = isLiPrice ? container?.querySelector('[ref="priceContainer"]') || container : container;

      if (this.subscribeRadio?.checked) {
        // Show subscription price as main, variant base price as compare-at (strikethrough)
        const subPrice = this._getActiveSubPrice();
        if (subPrice !== null) {
          priceEl.textContent = this._formatMoney(subPrice);

          // Compare-at = variant base price (always show when subscribing)
          if (this._lastVariantPrice > subPrice) {
            if (!compareEl) {
              // Re-query in case DOM was swapped by variant:update
              compareEl = container?.querySelector('.' + compareClass);
            }
            if (!compareEl) {
              const wrapper = document.createElement('span');
              wrapper.setAttribute('role', 'group');
              compareEl = document.createElement('span');
              compareEl.className = compareClass;
              wrapper.appendChild(compareEl);
              const priceParent = priceEl.closest('[role="group"]') || priceEl;
              priceParent.parentNode.insertBefore(wrapper, priceParent);
            }
            compareEl.textContent = this._formatMoney(this._lastVariantPrice);
            compareEl.style.display = '';
            if (compareEl.parentElement) compareEl.parentElement.style.display = '';

            // Show % OFF badge — savings vs the one-time COMPARE-AT price (fallback to base price)
            const pctBase = (this._lastVariantCompareAt && this._lastVariantCompareAt > this._lastVariantPrice)
              ? this._lastVariantCompareAt
              : this._lastVariantPrice;
            const pct = Math.round(((pctBase - subPrice) / pctBase) * 100);
            if (isLiPrice && pct > 0) {
              if (!badgeEl) badgeEl = priceContainer.querySelector('.li-price__badge');
              if (!badgeEl) {
                badgeEl = document.createElement('span');
                badgeEl.className = 'li-price__badge';
                priceContainer.appendChild(badgeEl);
              }
              badgeEl.textContent = pct + '% OFF';
              badgeEl.style.display = '';
            }
          } else {
            // Sub price >= variant price (unlikely), hide compare & badge
            if (compareEl) {
              compareEl.style.display = 'none';
              if (compareEl.parentElement) compareEl.parentElement.style.display = 'none';
            }
            if (badgeEl) badgeEl.style.display = 'none';
          }
        }
      } else {
        // One-time: main price = variant price
        priceEl.textContent = this._formatMoney(this._lastVariantPrice);

        // Compare-at = variant compare_at_price (only if exists and higher)
        if (compareEl) {
          if (this._lastVariantCompareAt > this._lastVariantPrice) {
            compareEl.textContent = this._formatMoney(this._lastVariantCompareAt);
            compareEl.style.display = '';
            if (compareEl.parentElement) compareEl.parentElement.style.display = '';
          } else {
            compareEl.style.display = 'none';
            if (compareEl.parentElement) compareEl.parentElement.style.display = 'none';
          }
        } else if (this._lastVariantCompareAt > this._lastVariantPrice) {
          // No compare element exists but we need one — create it
          compareEl = document.createElement('span');
          compareEl.className = compareClass;
          compareEl.textContent = this._formatMoney(this._lastVariantCompareAt);
          const wrapper = document.createElement('span');
          wrapper.setAttribute('role', 'group');
          wrapper.appendChild(compareEl);
          const priceParent = priceEl.closest('[role="group"]') || priceEl;
          priceParent.parentNode.insertBefore(wrapper, priceParent);
        }

        // Show/hide % OFF badge for one-time compare-at
        if (isLiPrice) {
          if (this._lastVariantCompareAt > this._lastVariantPrice) {
            const pct = Math.round(((this._lastVariantCompareAt - this._lastVariantPrice) / this._lastVariantCompareAt) * 100);
            if (pct > 0) {
              if (!badgeEl) badgeEl = priceContainer.querySelector('.li-price__badge');
              if (!badgeEl) {
                badgeEl = document.createElement('span');
                badgeEl.className = 'li-price__badge';
                priceContainer.appendChild(badgeEl);
              }
              badgeEl.textContent = pct + '% OFF';
              badgeEl.style.display = '';
            }
          } else {
            if (!badgeEl) badgeEl = priceContainer.querySelector('.li-price__badge');
            if (badgeEl) badgeEl.style.display = 'none';
          }
        }
      }
    }

    _getActiveSubPrice() {
      if (!this._planData?.allocations || !this.currentVariant) return null;
      const alloc = this._planData.allocations[String(this.currentVariant)];
      if (!alloc || alloc.length === 0) return null;

      // Find allocation matching selected plan, or use first
      if (this.selectedPlan) {
        const match = alloc.find(a => String(a.planId) === String(this.selectedPlan));
        if (match) return match.price;
      }
      return alloc[0].price;
    }

    /* ------------------------------------------------------------------
       Event Handlers
       ------------------------------------------------------------------ */

    _onOneTimeChange(e) {
      this._handleOneTime(!!e.isTrusted);
      this._updateOptionStyles();
      this._updateMainPrice();
    }

    _onSubscribeChange() {
      this._initSubscriptionUI(this.selectedPlan);
      this._updateOptionStyles();
      this._updateMainPrice();
    }

    _onFreqClick(e) {
      const btn = e.currentTarget;
      this.freqButtons.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      this._updateSellingPlan(btn.dataset.planId);
      this._updateMainPrice();
    }

    _onVariantUpdate(e) {
      const variant = e.detail?.resource;
      if (!variant) return;

      this.currentVariant = String(variant.id);

      // Store variant base prices
      this._lastVariantPrice = variant.price || 0;
      this._lastVariantCompareAt = variant.compare_at_price || 0;

      // Re-ensure the hidden input exists (morph may have removed it)
      requestAnimationFrame(() => {
        // Re-query DOM refs in case morph replaced elements
        this._cacheDOMRefs();

        this._ensureHiddenInput();
        this._installGuards();

        // If subscribed, re-apply the selected plan
        if (this.subscribeRadio?.checked && this.selectedPlan) {
          this._updateSellingPlan(this.selectedPlan);
        }

        // Update selling plan widget prices
        this._updatePricesForVariant(variant);

        // Update main price block
        this._updateMainPrice();
      });
    }

    _updatePricesForVariant(variant) {
      if (!this._planData?.allocations) return;

      // Re-query in case DOM was morphed
      this._cacheDOMRefs();

      const alloc = this._planData.allocations[String(variant.id)];

      // Update one-time prices in the widget
      if (this.priceCurrent) {
        this.priceCurrent.textContent = this._formatMoney(variant.price);
      }
      if (this.priceCompare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.priceCompare.textContent = this._formatMoney(variant.compare_at_price);
          this.priceCompare.style.display = '';
        } else {
          this.priceCompare.style.display = 'none';
        }
      }

      // Update subscription prices in the widget
      if (alloc && alloc.length > 0) {
        const subPrice = alloc[0].price;
        if (this.subPriceCurrent) {
          this.subPriceCurrent.textContent = this._formatMoney(subPrice);
        }
        if (this.subPriceCompare) {
          this.subPriceCompare.textContent = this._formatMoney(variant.price);
        }

        // Update discount badge — savings % vs the one-time COMPARE-AT price (fallback to price)
        const discountEl = this.querySelector('[data-discount-percent]');
        const discountBase = (variant.compare_at_price && variant.compare_at_price > variant.price)
          ? variant.compare_at_price
          : variant.price;
        if (discountEl && discountBase > 0) {
          const discount = Math.round(((discountBase - subPrice) / discountBase) * 100);
          discountEl.textContent = `Save ${discount}%`;
        }
      }
    }

    _formatMoney(cents) {
      if (typeof cents !== 'number') return '';
      var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return this._moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    /* ------------------------------------------------------------------
       Guards — prevent Horizon DOM morphing from clearing selling_plan
       ------------------------------------------------------------------ */

    _installGuards() {
      const input = this._getSellingInput();
      if (!input) return;

      // 1) Lock .value setter to block programmatic clears while subscribed
      const desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (desc && !input.__liLockedSetter) {
        const widget = this;
        Object.defineProperty(input, 'value', {
          configurable: true,
          get() { return desc.get.call(this); },
          set(v) {
            if (widget.subscribeRadio?.checked && widget.selectedPlan && v === '' && !widget.allowClear) {
              return; // block unwanted clear
            }
            return desc.set.call(this, v);
          }
        });
        input.__liLockedSetter = true;
      }

      // 2) Attribute observer: if attribute is cleared, re-apply
      if (!input.__liAttrObserver) {
        const widget = this;
        const mo = new MutationObserver(() => {
          const val = input.getAttribute('value') || '';
          if (widget.subscribeRadio?.checked && widget.selectedPlan && val === '' && !widget.allowClear) {
            widget._updateSellingPlan(widget.selectedPlan);
          }
        });
        mo.observe(input, { attributes: true, attributeFilter: ['value'] });
        input.__liAttrObserver = mo;
      }

      // 3) DOM observer: if the input node gets replaced by morph, re-bind
      if (!this._domObserver) {
        const widget = this;
        this._domObserver = new MutationObserver(() => {
          const current = widget._getSellingInput();
          if (current) {
            // Re-install guards on the possibly-new input
            if (!current.__liLockedSetter) {
              widget._installGuards();
            }
            // Re-apply value if it was lost
            if (widget.subscribeRadio?.checked && widget.selectedPlan && !current.value) {
              widget._updateSellingPlan(widget.selectedPlan);
            }
          } else {
            // Input was removed entirely — re-inject
            widget._ensureHiddenInput();
            if (widget.subscribeRadio?.checked && widget.selectedPlan) {
              widget._updateSellingPlan(widget.selectedPlan);
            }
            widget._installGuards();
          }
        });
        this._domObserver.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  customElements.define('li-selling-plan-widget', LiSellingPlanWidget);
}
